/* =============================================================================
 * FOZOCO • Cart Guard v4.2
 * -----------------------------------------------------------------------------
 * Porta o "FOZOCO BUNDLE GUARD v3.4" (que hoje vive em UpCart > Custom HTML >
 * Scripts Before Load) para dentro do tema, e o torna independente do UpCart.
 *
 * Por que sair do UpCart:
 *   O v3.4 depende de upcartSubscribeItemRemoved / upcartRefreshCart. Quando o
 *   UpCart for desligado, esses globais somem e o guard morre em silêncio —
 *   sem erro visível, só pedidos errados.
 *
 * Por que não basta o que já existe em fozoco-custom.js:
 *   1) fozoco-custom.js só carrega em `template.name == 'product'`. Removendo o
 *      óculos pelo drawer numa coleção, nada sincroniza e a lente fica órfã.
 *   2) O drawer do Section Store vive em shadow DOM: cliques são retargeted
 *      (e.target vira <section-store--cart-block>, closest() devolve null) e um
 *      MutationObserver no body não enxerga lá dentro. O botão de checkout é
 *      <a class="cart-checkout-link"> SEM href, então a[href*="/checkout"]
 *      também nunca casa.
 *
 * Regras de negócio (idênticas ao v3.4):
 *   A) LENTE órfã (sem óculos no mesmo bundle)  -> remove a lente.
 *   B) ÓCULOS sem lente no mesmo bundle          -> NÃO remove o óculos; APAGA
 *      as propriedades de grau e deixa só a marca _fozoco_grau_removed. Sem
 *      isso o pedido sai com "Lente escolhida: GRAU ALTO" num óculos que o
 *      cliente pagou como armação simples.
 *      v4.1 arquivava em `_fozoco_removido_*` em vez de apagar. Deu errado no
 *      #FZC115040 (19/09/2026): o valor "RECEITA_PENDENTE" continuava no
 *      pedido, o Flow tagueou como receita pendente e a operação tratou uma
 *      armação simples como pedido de grau. v4.2 volta a apagar (como v3.4).
 *   C) Se apagar as propriedades não "colar" (o app de receita reinjeta), remove
 *      a linha e re-adiciona a mesma variante limpa.
 *
 * Todas as operações usam a `key` do line item, nunca o índice: o índice muda a
 * cada alteração e duas passagens concorrentes removeriam a linha errada.
 * ========================================================================== */

(function () {
  "use strict";

  if (window.__FOZOCO_CART_GUARD__) return;
  window.__FOZOCO_CART_GUARD__ = true;

  var DEBUG = false; // true => loga cada passo no console
  var MAX_PASSES = 6;

  var BUNDLE_ID_KEYS = ["_fozoco_bundle_id", "_fozoco_bundle", "fozoco_bundle_id"];
  var BUNDLE_ROLE_KEYS = ["_fozoco_bundle_role", "fozoco_bundle_role"];
  var CLEAN_MARK = "_fozoco_grau_removed";

  function log(msg) {
    if (!DEBUG) return;
    try { console.log("[FOZOCO GUARD] " + msg); } catch (_) {}
  }

  /* ---------------------------------------------------------------- helpers */

  function readProp(item, names) {
    var props = (item && item.properties) || {};
    for (var i = 0; i < names.length; i++) {
      if (props[names[i]]) return props[names[i]];
    }
    return null;
  }

  function bundleId(item) { return readProp(item, BUNDLE_ID_KEYS); }
  function bundleRole(item) {
    return String(readProp(item, BUNDLE_ROLE_KEYS) || "").toLowerCase();
  }

  function getCart() {
    return fetch("/cart.js", {
      headers: { Accept: "application/json", "Cache-Control": "no-cache" },
      credentials: "same-origin"
    }).then(function (r) { return r.json(); });
  }

  function cartPost(path, body) {
    return fetch(path, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      credentials: "same-origin",
      body: JSON.stringify(body)
    }).then(function (r) { return r.json(); });
  }

  function rolesByBundle(cart) {
    var roles = Object.create(null);
    (cart.items || []).forEach(function (item) {
      var id = bundleId(item);
      if (!id) return;
      roles[id] = roles[id] || Object.create(null);
      roles[id][bundleRole(item)] = true;
    });
    return roles;
  }

  function isOrphanLens(item, roles) {
    var id = bundleId(item);
    return !!id && bundleRole(item) === "lente" && !roles[id]["oculos"];
  }

  /* Primeira lente cuja quantidade nao bate com a do oculos do mesmo bundle.
   * Devolve null quando esta tudo alinhado. Um conserto por passagem, porque
   * cada /cart/change.js invalida as keys anteriores. */
  function findQuantityMismatch(cart) {
    var groups = Object.create(null);

    (cart.items || []).forEach(function (item) {
      var id = bundleId(item);
      if (!id) return;
      (groups[id] = groups[id] || []).push(item);
    });

    var found = null;
    Object.keys(groups).forEach(function (id) {
      if (found) return;

      var group = groups[id];
      var frames = group.filter(function (i) { return bundleRole(i) === "oculos"; });
      var lenses = group.filter(function (i) { return bundleRole(i) === "lente"; });
      if (!frames.length || !lenses.length) return; // caso estrutural, ja tratado acima

      var want = frames.reduce(function (sum, i) { return sum + i.quantity; }, 0);
      for (var i = 0; i < lenses.length; i++) {
        if (lenses[i].quantity !== want) {
          found = { key: lenses[i].key, from: lenses[i].quantity, to: want };
          return;
        }
      }
    });

    return found;
  }

  function isBrokenFrame(item, roles) {
    var id = bundleId(item);
    return !!id && bundleRole(item) === "oculos" && !roles[id]["lente"];
  }

  /* Óculos ficou sem lente: apaga TUDO que veio do fluxo de grau e deixa só a
   * marca _fozoco_grau_removed. Nada de arquivar em `_fozoco_removido_*`
   * (v4.1): qualquer propriedade com o valor "RECEITA_PENDENTE" dentro, mesmo
   * renomeada, ainda casa com o Flow/automação que tagueia o pedido como
   * receita pendente. Uma armação comprada sem lente tem que chegar no admin
   * igual a qualquer armação simples.
   *
   * Apagar as de CONTROLE (`_fozoco_bundle_id` e `_fozoco_bundle_role`)
   * também é obrigatório: se elas ficassem, isBrokenFrame() continuaria
   * casando na passagem seguinte e o guard entraria no loop que derrubou o
   * v3.3. Propriedade que começa com "_" fica escondida do cliente.
   *
   * /cart/change.js substitui o objeto `properties` inteiro, então o que não
   * for devolvido aqui some da linha. */
  function archiveProps(item) {
    var out = {};
    out[CLEAN_MARK] = "true";
    return out;
  }

  /* ------------------------------------------------------------ enforcement */

  /* Uma passagem por vez, sempre relendo o carrinho: cada mutação invalida as
   * keys/índices anteriores. `escalate` = a limpeza de propriedades não colou na
   * passagem anterior, então parte para remover-e-readicionar. */
  function enforce(cart, pass, escalate) {
    if (pass > MAX_PASSES) {
      log("desisti após " + MAX_PASSES + " passagens — propriedades reinjetadas server-side.");
      return Promise.resolve("failed");
    }

    var roles = rolesByBundle(cart);
    var items = cart.items || [];

    for (var i = 0; i < items.length; i++) {
      var item = items[i];

      if (isOrphanLens(item, roles)) {
        log("passagem " + pass + ": lente órfã -> removendo");
        return cartPost("/cart/change.js", { id: item.key, quantity: 0 })
          .then(getCart)
          .then(function (c) { return enforce(c, pass + 1, false); });
      }

      if (isBrokenFrame(item, roles)) {
        if (!escalate) {
          log("passagem " + pass + ": óculos sem lente -> limpando propriedades de grau");
          return cartPost("/cart/change.js", {
            id: item.key,
            quantity: item.quantity,
            properties: archiveProps(item)
          })
            .then(getCart)
            .then(function (c) {
              var r2 = rolesByBundle(c);
              var stillBroken = (c.items || []).some(function (it) {
                return isBrokenFrame(it, r2);
              });
              if (stillBroken) log("limpeza não colou -> escalando para remover-e-readicionar");
              return enforce(c, pass + 1, stillBroken);
            });
        }

        log("passagem " + pass + ": ESCALAÇÃO -> removendo e readicionando variante " + item.variant_id);
        var variantId = item.variant_id;
        var qty = item.quantity;
        var archived = archiveProps(item); // captura antes de remover a linha
        return cartPost("/cart/change.js", { id: item.key, quantity: 0 })
          .then(function () {
            return cartPost("/cart/add.js", {
              items: [{ id: variantId, quantity: qty, properties: archived }]
            });
          })
          .then(getCart)
          .then(function (c) { return enforce(c, pass + 1, false); });
      }
    }

    /* D) Quantidades dessincronizadas dentro do bundle.
     *
     * Nem o v3.4 do UpCart nem o syncBundledItemsOnce do fozoco-custom.js olhavam
     * quantidade — os dois so verificavam presenca/ausencia dos papeis. Resultado:
     * baixar o oculos de 2 para 1 no drawer deixava a lente em 2, e o cliente
     * pagava 1 armacao + 2 lentes. A lente sempre segue a quantidade do oculos. */
    var mismatch = findQuantityMismatch(cart);
    if (mismatch) {
      log("passagem " + pass + ": lente x" + mismatch.from + " != oculos x" + mismatch.to + " -> alinhando");
      return cartPost("/cart/change.js", { id: mismatch.key, quantity: mismatch.to })
        .then(getCart)
        .then(function (c) { return enforce(c, pass + 1, false); });
    }

    log("carrinho consistente (" + (pass - 1) + " correções)");
    return Promise.resolve(pass > 1 ? "fixed" : "clean");
  }

  /* Lock compartilhado com a sincronização legada de fozoco-custom.js, que roda
   * nas páginas de produto e remove por índice. Sem o lock as duas rodam juntas
   * e o /cart/change.js concorrente apaga a linha errada. */
  function sync() {
    // Outra chamada do fluxo de lentes começou enquanto esperávamos: adia de novo.
    try {
      if ((Number(window.__fozoco_bundle_ignore_until) || 0) > Date.now()) {
        schedule();
        return Promise.resolve("deferred");
      }
    } catch (_) {}

    /* Ocupado: marca como sujo em vez de descartar. Uma alteracao do cliente que
     * caia no meio de uma sincronizacao anterior ficaria sem verificacao nenhuma
     * — foi assim que mexer na quantidade do oculos deixava a lente para tras.
     * A releitura acontece assim que a passagem atual termina, em vez de ficar
     * repolling num intervalo fixo. */
    if (window.__fozocoBundleSyncLock === true) {
      dirtyDuringSync = true;
      /* O reagendamento por bandeira so dispara quando quem termina somos NOS.
       * Nas paginas de produto o lock pode estar com a sincronizacao legada do
       * fozoco-custom.js, que solta o lock sem nos avisar — sem este timer a
       * verificacao ficava perdida para sempre. */
      schedule(700);
      return Promise.resolve("busy");
    }
    window.__fozocoBundleSyncLock = true;
    dirtyDuringSync = false;

    return getCart()
      .then(function (cart) { return enforce(cart, 1, false); })
      .catch(function (e) { log("erro: " + e); return "error"; })
      .then(function (result) {
        window.__fozocoBundleSyncLock = false;
        if (result === "fixed") refreshCartUi();

        // Mudou o carrinho enquanto rodavamos: relê agora, sem esperar timeout.
        if (dirtyDuringSync) {
          dirtyDuringSync = false;
          schedule(120);
        }
        return result;
      });
  }

  /* Antes do checkout: se outra passagem está em voo (lock ocupado), sync()
   * devolvia "busy" na hora e o clique seguia para o checkout com a limpeza
   * pela metade. Aqui espera o lock soltar (até ~2s) e só então roda a
   * verificação final. O timeout de 2500ms do clique continua valendo. */
  function syncWhenIdle(waited) {
    waited = waited || 0;
    var busy = window.__fozocoBundleSyncLock === true ||
      (Number(window.__fozoco_bundle_ignore_until) || 0) > Date.now();
    if (busy && waited < 2000) {
      return new Promise(function (resolve) {
        setTimeout(function () { resolve(syncWhenIdle(waited + 100)); }, 100);
      });
    }
    return sync();
  }

  function refreshCartUi() {
    try {
      if (window.SectionStoreCart && typeof window.SectionStoreCart.refresh === "function") {
        window.SectionStoreCart.refresh();
      }
    } catch (_) {}
    try {
      document.dispatchEvent(new CustomEvent("theme:cart:change", { bubbles: true, detail: {} }));
    } catch (_) {}
  }

  /* ------------------------------------------------- gatilhos: Section Store */

  /* ATENÇÃO: onBeforeCheckout NÃO é assíncrono. A implementação do app é
   *
   *   everyCallbackAllows(t,e){ return t.every(r => this.answerFrom(r,e) !== false) }
   *
   * ou seja: ela só olha se o retorno é `false`. Devolver uma Promise ali passa
   * batido (Promise !== false) e o checkout segue sem esperar a limpeza. Por
   * isso o hook é usado apenas como VETO síncrono, e quem realmente segura o
   * clique é o interceptador com composedPath() mais abaixo. */
  var wiredSectionStore = false;

  function wireSectionStore() {
    if (wiredSectionStore) return true;
    var api = window.SectionStoreCart;
    if (!api || typeof api.onBeforeCheckout !== "function") return false;

    wiredSectionStore = true;
    log("Section Store detectado — hook de veto + subscribe ligados.");

    try {
      // Veto síncrono: enquanto uma limpeza está em voo, nada de checkout.
      api.onBeforeCheckout(function () {
        return window.__fozocoBundleSyncLock !== true;
      });
    } catch (_) {}

    try {
      if (typeof api.subscribe === "function") {
        api.subscribe(function () { schedule(350); });
      }
    } catch (_) {}

    return true;
  }

  /* --------------------------------------------- gatilhos: qualquer carrinho */

  var CART_MUTATION = /\/cart\/(add|change|update|clear)(\.js)?/;
  var dirtyDuringSync = false;
  var timer = null;

  /* Janela de "bundle em voo": fozoco-custom.js grava
   *   window.__fozoco_bundle_ignore_until = Date.now() + 6000
   * antes de cada /cart/add.js do fluxo de lentes. Quando a receita vai como
   * arquivo, óculos e lente são DUAS chamadas (multipart não aceita `items`), e
   * entre elas o carrinho tem um óculos sem lente por ~1s. Sem esta espera o
   * guard rodava 400ms após a primeira chamada, arquivava as propriedades do
   * óculos e, quando a lente chegava, a removia como órfã — cliente ficava só
   * com a armação. Aqui a verificação é adiada até a janela fechar. */
  function bundleQuietDelay(delay) {
    var base = typeof delay === "number" ? delay : 400;
    var until = 0;
    try { until = Number(window.__fozoco_bundle_ignore_until) || 0; } catch (_) {}
    var remaining = until - Date.now();
    return remaining > 0 ? Math.max(base, remaining + 300) : base;
  }

  function schedule(delay) {
    clearTimeout(timer);
    timer = setTimeout(sync, bundleQuietDelay(delay));
  }

  (function patchFetch() {
    var original = window.fetch;
    if (typeof original !== "function") return;

    window.fetch = function (input) {
      var url = typeof input === "string" ? input : (input && input.url) || "";
      var isCartMutation = CART_MUTATION.test(String(url));
      var promise = original.apply(this, arguments);

      /* Agenda SEMPRE. As nossas proprias chamadas caem no lock e viram um
       * reagendamento; a passagem seguinte encontra o carrinho consistente,
       * devolve "clean" sem escrever nada e a cadeia para. */
      if (isCartMutation) {
        try { promise.then(function () { schedule(); }, function () {}); }
        catch (_) { schedule(); }
      }
      return promise;
    };
  })();

  (function patchXHR() {
    var open = XMLHttpRequest.prototype.open;
    var send = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function (method, url) {
      try { this.__fozocoUrl = url; } catch (_) {}
      return open.apply(this, arguments);
    };

    XMLHttpRequest.prototype.send = function () {
      try {
        this.addEventListener("loadend", function () {
          if (CART_MUTATION.test(String(this.__fozocoUrl || ""))) schedule();
        });
      } catch (_) {}
      return send.apply(this, arguments);
    };
  })();

  /* Fallback de checkout para o drawer nativo do Pipeline e para o caso de o
   * hook do Section Store não estar disponível. composedPath() é obrigatório:
   * dentro de shadow DOM o e.target vira o host. */
  var CHECKOUT_SELECTORS = [
    "a.cart-checkout-link",
    'button[name="checkout"]',
    '[name="checkout"]',
    "[data-checkout]"
  ].join(",");

  function findCheckoutTrigger(path) {
    for (var i = 0; i < path.length; i++) {
      var node = path[i];
      if (!node || node.nodeType !== 1) continue;
      try {
        if (node.matches && node.matches(CHECKOUT_SELECTORS)) return node;
        var href = node.getAttribute && node.getAttribute("href");
        if (href && href.indexOf("/checkout") === 0) return node;
      } catch (_) {}
    }
    return null;
  }

  /* Este é o guard de verdade — vale para o drawer nativo E para o Section
   * Store. Roda na fase de captura no document, então pega o clique antes de
   * qualquer handler do app, inclusive os de dentro do shadow root. */
  document.addEventListener(
    "click",
    function (event) {
      var path = event.composedPath ? event.composedPath() : [event.target];
      var trigger = findCheckoutTrigger(path);
      if (!trigger) return;

      // Preserva o destino real do app (pode carregar desconto/atributos).
      var target = "/checkout";
      try {
        var href = trigger.getAttribute && trigger.getAttribute("href");
        if (href && href.indexOf("/") === 0) target = href;
      } catch (_) {}

      event.preventDefault();
      event.stopPropagation();

      var done = false;
      function go() {
        if (done) return;
        done = true;
        window.location.href = target;
      }

      // Nunca prender o cliente: se a limpeza travar, o checkout segue assim mesmo.
      setTimeout(go, 2500);
      syncWhenIdle().then(go, go);
    },
    true
  );

  /* --------------------------------------------------------------- bootstrap */

  // A API do Section Store aparece depois do script do app; tenta por ~15s.
  (function waitForSectionStore(tries) {
    if (wireSectionStore()) return;
    if (tries >= 30) { log("Section Store não encontrado — usando só os fallbacks."); return; }
    setTimeout(function () { waitForSectionStore(tries + 1); }, 500);
  })(0);

  // Pega carrinhos que já ficaram inconsistentes numa sessão anterior.
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () { schedule(900); });
  } else {
    schedule(900);
  }

  window.FOZOCO_CART_GUARD = { sync: sync, enforce: enforce, version: "4.2" };
})();
